<template>
  <div>
    <div class="ibox">
      <div class="ibox-title">
        <h5>{{ $t('chainProduct.queryCondition') }}</h5>
        <div class="ibox-tools" style="top:10px;"></div>
      </div>
      <div class="ibox-content">
        <el-row :gutter="20">
          <el-col :span="6">
            <el-select 
              v-model="chainProductInfo.conditions.categoryId" 
              :placeholder="$t('chainProduct.selectCategory')"
              style="width:100%"
            >
              <el-option 
                v-for="item in chainProductInfo.catalogs" 
                :key="item.categoryId"
                :label="item.categoryName" 
                :value="item.categoryId"
              />
            </el-select>
          </el-col>
          <el-col :span="8">
            <el-input 
              v-model="chainProductInfo.conditions.prodName" 
              :placeholder="$t('chainProduct.inputProductName')"
            />
          </el-col>
          <el-col :span="6">
            <el-select 
              v-model="chainProductInfo.conditions.state" 
              :placeholder="$t('chainProduct.selectStatus')"
              style="width:100%"
            >
              <el-option 
                v-for="item in chainProductInfo.states" 
                :key="item.statusCd"
                :label="item.name" 
                :value="item.statusCd"
              />
            </el-select>
          </el-col>
          <el-col :span="4">
            <el-button type="primary" size="small" @click="_queryProductMethod">
              <i class="el-icon-search"></i> {{ $t('common.query') }}
            </el-button>
            <el-button size="small" @click="_resetProductMethod">
              <i class="el-icon-refresh"></i> {{ $t('common.reset') }}
            </el-button>
          </el-col>
        </el-row>
      </div>
    </div>

    <div class="ibox">
      <div class="ibox-title">
        <h5>{{ $t('chainProduct.productInfo') }}</h5>
        <div class="ibox-tools" style="top:10px;">
          <el-button size="small" @click="_goBack">{{ $t('common.back') }}</el-button>
          <el-button type="primary" size="small" @click="_openAddProductModal">
            <i class="el-icon-plus"></i> {{ $t('common.add') }}
          </el-button>
        </div>
      </div>
      <div class="ibox-content">
        <el-table 
          :data="chainProductInfo.products" 
          border 
          style="width:100%"
        >
          <el-table-column prop="productId" :label="$t('chainProduct.productId')" align="center" />
          <el-table-column :label="$t('chainProduct.productImage')" align="center">
            <template #default="{row}">
              <el-image 
                style="width:50px;height:50px" 
                :src="row.coverPhoto" 
                :preview-src-list="[row.coverPhoto]"
                fit="cover"
              >
                <template #error>
                  <div class="image-slot">
                    <el-image :src="'/img/noPhoto.jpg'" style="width:50px;height:50px" />
                  </div>
                </template>
              </el-image>
            </template>
          </el-table-column>
          <el-table-column prop="catalogName" :label="$t('chainProduct.productCategory')" align="center" />
          <el-table-column prop="prodName" :label="$t('chainProduct.productName')" align="center" />
          <el-table-column prop="unitName" :label="$t('chainProduct.productUnit')" align="center" />
          <el-table-column prop="sort" :label="$t('chainProduct.sort')" align="center" />
          <el-table-column :label="$t('chainProduct.status')" align="center">
            <template #default="{row}">
              {{ row.state == '2002' ? $t('chainProduct.onShelf') : $t('chainProduct.offShelf') }}
            </template>
          </el-table-column>
          <el-table-column prop="createTime" :label="$t('chainProduct.createTime')" align="center" />
          <el-table-column :label="$t('common.operation')" align="center" width="180">
            <template #default="{row}">
              <el-button size="mini" @click="_openEditProductModel(row)">{{ $t('common.edit') }}</el-button>
              <el-button size="mini" type="danger" @click="_openDeleteProductModel(row)">
                {{ $t('common.delete') }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <el-pagination
          @current-change="handleCurrentChange"
          :current-page="chainProductInfo.conditions.page"
          :page-size="chainProductInfo.conditions.row"
          layout="total, prev, pager, next"
          :total="chainProductInfo.total"
        />
      </div>
    </div>

    <edit-product ref="editProduct" @refresh="_listProducts" />
    <delete-product ref="deleteProduct" @refresh="_listProducts" />
  </div>
</template>

<script>
import { listChainProduct, listChainSupplierCatalog } from '@/api/admin/chainProduct/chainProductApi'
import EditProduct from '@/components/admin/chainProduct/EditProduct'
import DeleteProduct from '@/components/admin/chainProduct/DeleteProduct'
import { getShopId } from '@/api/shop/shopApi'

export default {
  name: 'ChainProduct',
  components: { EditProduct, DeleteProduct },
  data() {
    return {
      DEFAULT_PAGE: 1,
      DEFAULT_ROWS: 10,
      chainProductInfo: {
        products: [],
        total: 0,
        catalogs: [],
        conditions: {
          categoryId: '',
          state: '',
          prodName: '',
          keyword: '',
          barCode: '',
          csId: '',
          page: 1,
          row: 10
        },
        states: [
          { statusCd: '2002', name: this.$t('chainProduct.onShelf') },
          { statusCd: '2003', name: this.$t('chainProduct.offShelf') }
        ]
      }
    }
  },
  created() {
    this.chainProductInfo.conditions.csId = this.$route.query.csId
    this._listProducts(this.DEFAULT_PAGE, this.DEFAULT_ROWS)
    this._listProductCategorys()
  },
  methods: {
    handleCurrentChange(val) {
      this._listProducts(val, this.DEFAULT_ROWS)
    },
    async _listProducts(page, rows) {
      this.chainProductInfo.conditions.page = page
      this.chainProductInfo.conditions.row = rows
      const params = {
        ...this.chainProductInfo.conditions,
        prodName: this.chainProductInfo.conditions.prodName.trim()
      }
      
      try {
        const res = await listChainProduct(params)
        this.chainProductInfo.total = res.total
        this.chainProductInfo.products = res.data
      } catch (error) {
        console.error('Failed to fetch products:', error)
      }
    },
    async _listProductCategorys() {
      const params = {
        page: 1,
        row: 50,
        shopId: getShopId()
      }
      
      try {
        const res = await listChainSupplierCatalog(params)
        this.chainProductInfo.catalogs = res.data
      } catch (error) {
        console.error('Failed to fetch categories:', error)
      }
    },
    _openAddProductModal() {
      this.$router.push({
        path: '/admin/addChainProduct',
        query: { csId: this.chainProductInfo.conditions.csId }
      })
    },
    _openEditProductModel(product) {
      this.$refs.editProduct.open(product)
    },
    _openDeleteProductModel(product) {
      this.$refs.deleteProduct.open(product)
    },
    _queryProductMethod() {
      this._listProducts(this.DEFAULT_PAGE, this.DEFAULT_ROWS)
    },
    _resetProductMethod() {
      this.chainProductInfo.conditions.prodName = ''
      this.chainProductInfo.conditions.categoryId = ''
      this.chainProductInfo.conditions.state = ''
      this._listProducts(this.DEFAULT_PAGE, this.DEFAULT_ROWS)
    },
    _goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.ibox {
  margin-bottom: 20px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 1px rgba(0,0,0,.1);
}
.ibox-title {
  padding: 15px 15px 7px;
  min-height: 48px;
  border-bottom: 1px solid #e7eaec;
}
.ibox-content {
  padding: 15px 20px 20px;
}
</style>